import { CheckCircle, XCircle } from "lucide-react";
import React, { useState } from "react";

export interface RequirementMatch {
  requirement: string;
  is_met: boolean;
  explanation: string;
  category: string;
}

export interface ProfileMatchResultData {
  match_percentage: number;
  requirements: RequirementMatch[];
}

interface ProfileMatchResultProps {
  result: ProfileMatchResultData;
}

export const ProfileMatchResult: React.FC<ProfileMatchResultProps> = ({
  result,
}) => {
  const { match_percentage, requirements } = result;
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Group requirements by category
  const groupedRequirements = requirements.reduce(
    (acc, req) => {
      const category = req.category || "other";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(req);
      return acc;
    },
    {} as Record<string, RequirementMatch[]>
  );

  // Calculate met count
  const metCount = requirements.filter((r) => r.is_met).length;

  // Get assessment text based on percentage
  const getAssessment = () => {
    if (match_percentage >= 80) {
      return {
        text: "Excellent match! Your profile aligns very well with this position.",
        color: "text-green-700",
      };
    } else if (match_percentage >= 60) {
      return {
        text: "Good match. You meet most of the requirements.",
        color: "text-blue-700",
      };
    } else if (match_percentage >= 40) {
      return {
        text: "Partial match. Consider tailoring your CV to emphasize relevant skills.",
        color: "text-yellow-700",
      };
    } else {
      return {
        text: "Limited match. This role may require additional skills or experience.",
        color: "text-red-700",
      };
    }
  };

  const assessment = getAssessment();

  // Get gauge color based on percentage
  const getGaugeColor = () => {
    if (match_percentage >= 80) return "#22c55e"; // green-500
    if (match_percentage >= 60) return "#3b82f6"; // blue-500
    if (match_percentage >= 40) return "#eab308"; // yellow-500
    return "#ef4444"; // red-500
  };

  // Category display names
  const categoryNames: Record<string, string> = {
    skills: "Skills",
    experience: "Experience",
    education: "Education",
    other: "Other Requirements",
  };

  // Category order
  const categoryOrder = ["skills", "experience", "education", "other"];

  return (
    <div className="space-y-4">
      {/* Match Percentage Gauge */}
      <div className="flex flex-col items-center py-3">
        <div className="relative w-28 h-28">
          <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="10"
            />
            <circle
              cx="50"
              cy="50"
              r="45"
              fill="none"
              stroke={getGaugeColor()}
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={`${match_percentage * 2.83} 283`}
              className="transition-all duration-1000 ease-out"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl font-bold text-jobesta-gray-90">
              {match_percentage}%
            </span>
          </div>
        </div>
        <p className={`mt-2 text-xs font-medium text-center ${assessment.color}`}>
          {assessment.text}
        </p>
      </div>

      {/* Summary */}
      <div className="bg-jobesta-gray-30 rounded-jobesta p-3">
        <p className="text-sm text-jobesta-gray-70">
          You meet <strong>{metCount}</strong> out of <strong>{requirements.length}</strong>{" "}
          requirements
        </p>
      </div>

      {/* Requirements by Category */}
      <div className="space-y-2">
        {categoryOrder
          .filter((cat) => groupedRequirements[cat]?.length > 0)
          .map((category) => {
            const reqs = groupedRequirements[category];
            const metInCategory = reqs.filter((r) => r.is_met).length;
            const isExpanded = expandedCategory === category;

            return (
              <div key={category} className="border border-gray-200 rounded-jobesta overflow-hidden">
                <button
                  onClick={() =>
                    setExpandedCategory(isExpanded ? null : category)
                  }
                  className="w-full flex items-center justify-between p-2.5 bg-jobesta-gray-30 hover:bg-jobesta-gray-40 transition-colors"
                >
                  <span className="text-sm font-medium text-jobesta-gray-90">
                    {categoryNames[category] || category}
                  </span>
                  <span className="text-xs text-jobesta-gray-60">
                    {metInCategory}/{reqs.length} met
                  </span>
                </button>
                {isExpanded && (
                  <div className="p-2.5 space-y-2 bg-white">
                    {reqs.map((req, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 p-2 rounded-jobesta bg-jobesta-gray-30"
                      >
                        {req.is_met ? (
                          <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-jobesta-gray-90">
                            {req.requirement}
                          </p>
                          {req.explanation && (
                            <p className="text-xs text-jobesta-gray-60 mt-1">
                              {req.explanation}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default ProfileMatchResult;
